// ABOUTME: Tracks daily outfit wear logs for streak and usage analytics
// ABOUTME: Cross-updates wardrobeStore items and awards XP via gameStore

import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { XP_REWARDS } from '@/constants/gamification'
import { getSyncService } from '@/services/SyncService'
import { useGameStore } from './gameStore'
import { useWardrobeStore } from './wardrobeStore'

export interface WearLog {
  id: string
  date: string
  itemIds: string[]
  outfitId?: string
  occasion?: string
  rating?: number
  source: 'manual' | 'suggestion'
}

interface WearLogState {
  logs: WearLog[]
}

interface WearLogActions {
  logWear: (entry: Omit<WearLog, 'id'>) => WearLog
  getLogForDate: (date: string) => WearLog | undefined
  getLogsForItem: (itemId: string) => WearLog[]
  hasLoggedToday: () => boolean
  getStreak: () => number
  deleteLog: (logId: string) => void
  clearAll: () => void
}

type WearLogStore = WearLogState & WearLogActions

const initialState: WearLogState = {
  logs: [],
}

function getToday(): string {
  return new Date().toISOString().split('T')[0]
}

export const useWearLogStore = create<WearLogStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      logWear: (entry) => {
        const isFirstLogToday = !get().logs.some((l) => l.date === entry.date)

        const newLog: WearLog = {
          ...entry,
          id: crypto.randomUUID(),
        }

        set((state) => ({
          logs: [...state.logs, newLog],
        }))

        const wardrobeStore = useWardrobeStore.getState()
        for (const itemId of entry.itemIds) {
          const item = wardrobeStore.items.find((i) => i.id === itemId)
          if (item) {
            wardrobeStore.updateItem(itemId, {
              lastWorn: entry.date,
              timesWorn: (item.timesWorn || 0) + 1,
            })
          }
        }

        if (isFirstLogToday && entry.date === getToday()) {
          const gameStore = useGameStore.getState()
          gameStore.addExperience(XP_REWARDS.OUTFIT_LOGGED)
          gameStore.addActivity({
            type: 'outfit_logged',
            message: `Logged today's outfit`,
          })
        }

        getSyncService().queueOperation('wearlog', 'create', newLog)

        return newLog
      },

      getLogForDate: (date) => {
        return get().logs.find((l) => l.date === date)
      },

      getLogsForItem: (itemId) => {
        return get().logs.filter((l) => l.itemIds.includes(itemId))
      },

      hasLoggedToday: () => {
        const today = getToday()
        return get().logs.some((l) => l.date === today)
      },

      getStreak: () => {
        const { logs } = get()
        if (logs.length === 0) return 0

        const logDates = new Set(logs.map((l) => l.date))
        const today = new Date()
        let streak = 0
        const checkDate = new Date(today)

        while (true) {
          const dateStr = checkDate.toISOString().split('T')[0]
          if (logDates.has(dateStr)) {
            streak++
            checkDate.setDate(checkDate.getDate() - 1)
          } else {
            break
          }
        }

        return streak
      },

      deleteLog: (logId) => {
        set((state) => ({
          logs: state.logs.filter((l) => l.id !== logId),
        }))
      },

      clearAll: () => {
        set(initialState)
      },
    }),
    {
      name: 'gentleman-wearlog-store',
      partialize: (state) => ({
        logs: state.logs,
      }),
    }
  )
)
